// Category-aware optional product attributes for the Stock Inventory
// add/edit forms.
//
// These four fields used to render unconditionally, so picking
// "Consumables" (e.g. masking tape) still asked for "Shade/VLT" — a
// window-film-only measure (Visible Light Transmission %) that means
// nothing for tape, chemicals, blades or tools.
//
// The map below was sanity-checked against every product in the DB
// (51 products, Sep 2026): `shade_vlt`, `colour` and `size` are
// populated ONLY on Films/Wraps, while `variant_type` is populated on
// 100% of products in every category. No existing product holds a value
// in a field this map hides.

export const OPTIONAL_FIELD_CONFIG = [
  { key: "variant_type", label: "Variant Type", placeholder: "e.g., Ceramic, Performance" },
  { key: "shade_vlt", label: "Shade/VLT", placeholder: "e.g., 5%, 20%, 35%" },
  { key: "colour", label: "Colour", placeholder: "e.g., Black, Blue" },
  { key: "size", label: "Size/Width", placeholder: "e.g., 61cm, 152cm" },
];

export const ALL_OPTIONAL_FIELD_KEYS = OPTIONAL_FIELD_CONFIG.map(f => f.key);

export const CATEGORY_OPTIONAL_FIELDS = {
  "Films/Wraps": ["variant_type", "shade_vlt", "colour", "size"],
  "Chemicals/Liquids": ["variant_type"],
  "Blades/Cutting tools": ["variant_type", "size"],
  "Tools/Equipment": ["variant_type"],
  "Consumables": ["variant_type", "colour", "size"],
  "Other": ["variant_type", "shade_vlt", "colour", "size"],
};

// Unknown/legacy/blank category → show everything. Safer to over-offer
// than to hide a field someone needs, and it keeps older rows (e.g. a
// legacy "Chemicals" label) fully editable.
export const allowedOptionalFields = (category) =>
  CATEGORY_OPTIONAL_FIELDS[category] || ALL_OPTIONAL_FIELD_KEYS;

// A field shows if the category allows it OR the product already has a
// value saved in it — never silently hide data someone already entered.
export const visibleOptionalFields = (category, data) => {
  const allowed = allowedOptionalFields(category);
  return OPTIONAL_FIELD_CONFIG.filter(
    f => allowed.includes(f.key) || String(data?.[f.key] ?? "").trim() !== ""
  );
};

// True when a field is only on screen because of an existing saved
// value, so the UI can explain why it's there.
export const isKeptForExistingValue = (category, fieldKey) =>
  !allowedOptionalFields(category).includes(fieldKey);

// Blank out the fields that don't apply to a newly-picked category, so
// a stray "35%" typed under Films/Wraps can't be saved invisibly after
// switching to Consumables. Used by the ADD form only — on EDIT we
// leave saved values alone and let the grandfathering rule surface them.
export const clearedHiddenFields = (category) => {
  const allowed = allowedOptionalFields(category);
  const cleared = {};
  ALL_OPTIONAL_FIELD_KEYS.forEach(k => {
    if (!allowed.includes(k)) cleared[k] = "";
  });
  return cleared;
};
